import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { HStack, LookoButton, LookoModal, VStack } from '@looko-design-system';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Link, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { 자동주문취소_스케줄러_시간 } from '@/constants/consts';
import { claimMap } from '@/router/claimMap';
import { orderMap } from '@/router/orderMap';

import useAclosetOrderCountData from './useAclosetOrderCountData';

const LOCAL_STORAGE_KEY = 'lastClickDate-aclosetOrder' as const;
const lastClickDateAtom = atomWithStorage(LOCAL_STORAGE_KEY, '');

/**
 * 세컨솔드 주문 확인 모달
 *
 * 노출 조건:
 *    1. 미처리 주문/반품 카운트가 0개 이상이고, 미처리 알림 확인이 하루가 지났을 경우 자동 노출 (로컬스토리지에 클릭 날짜 저장)
 *    2. GNB의 미처리 주문 알람 클릭한 경우 노출 (로컬스토리지에 저장하지 않음)
 *
 * @props
 *    - 세컨솔드_알람_모달상태: GNB의 세컨솔드 미처리 알람 클릭시, 넘겨받는 open (Optional)
 *    - onCloseProps: GNB의 세컨솔드 미처리 알람 클릭시, 넘겨받는 onClose (Optional)
 *    - 로컬스토리지에_저장하는가: 하루에 한 번 뜨는 모달에서만 True, GNB의 세컨솔드 미처리 알림에서는 False
 */
export default function AclosetOrderModal({
  로컬스토리지에_저장하는가,
  세컨솔드_미처리알람클릭_모달상태 = false,
  세컨솔드_미처리알람클릭_모달닫기,
}: {
  로컬스토리지에_저장하는가: boolean;
  세컨솔드_미처리알람클릭_모달상태?: boolean;
  세컨솔드_미처리알람클릭_모달닫기?: () => void;
}) {
  // route
  const location = useLocation();
  const navigate = useNavigate();

  // hook
  const { 상품준비중_카운트, 할인요청_카운트, 배송준비중_카운트, 반품신청_카운트, totalCount } =
    useAclosetOrderCountData();

  // jotai
  const [lastClickDate, setLastClickDate] = useAtom(lastClickDateAtom);

  // local
  const [매일노출_모달상태, 매일노출_모달상태_변경하기] = React.useState(
    dayjs().diff(dayjs(lastClickDate), 'day') > 0 && totalCount > 0,
  );

  // handler
  const handleClickButton = () => {
    if (로컬스토리지에_저장하는가) {
      setLastClickDate(dayjs().format('YYYY.MM.DD'));
    }

    매일노출_모달상태_변경하기(false);
    세컨솔드_미처리알람클릭_모달닫기?.();
  };

  const handleClickLink = (url: string) => {
    navigate(url);
    handleClickButton();
  };

  React.useEffect(() => {
    매일노출_모달상태_변경하기(dayjs().diff(dayjs(lastClickDate), 'day') > 0 && totalCount > 0);
  }, [lastClickDate, location.pathname, totalCount]);

  return (
    <LookoModal open={매일노출_모달상태 || 세컨솔드_미처리알람클릭_모달상태}>
      <LookoModal.Header>세컨솔드 주문 확인 알림</LookoModal.Header>
      <LookoModal.Content>
        <VStack spacing={2} width={500}>
          <Typography variant="Subtitle_Looko">처리가 필요한 세컨솔드 주문이 있습니다.</Typography>

          <VStack>
            <HStack alignItems={'center'} spacing={1}>
              <Typography variant="Body_Looko">· 상품 준비중 주문 {상품준비중_카운트}건 (주문확인 필요)</Typography>
              <Link component={'button'} onClick={() => handleClickLink(orderMap['payment-done'].path)}>
                <Typography variant="Body_Looko">바로가기</Typography>
              </Link>
            </HStack>

            <HStack alignItems={'center'} spacing={1}>
              <Typography variant="Body_Looko">· 할인 요청 상품 {할인요청_카운트}건 (요청확인 필요)</Typography>
              <Link component={'button'} onClick={() => handleClickLink(orderMap['discount-offer'].path)}>
                <Typography variant="Body_Looko">바로가기</Typography>
              </Link>
            </HStack>

            <HStack alignItems={'center'} spacing={1}>
              <Typography variant="Body_Looko">
                · 배송 준비중 주문 {배송준비중_카운트}건 (운송장번호 입력 필요)
              </Typography>
              <Link component={'button'} onClick={() => handleClickLink(orderMap['shipping-pending'].path)}>
                <Typography variant="Body_Looko">바로가기</Typography>
              </Link>
            </HStack>

            <HStack alignItems={'center'} spacing={1}>
              <Typography variant="Body_Looko">
                · 반품 처리 필요 {반품신청_카운트}건 (택배사에 직접 접수해주셔야 합니다.)
              </Typography>
              <Link component={'button'} onClick={() => handleClickLink(claimMap['return'].path)}>
                <Typography variant="Body_Looko">바로가기</Typography>
              </Link>
            </HStack>
          </VStack>

          <HStack alignItems={'center'} spacing={0.5}>
            <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
            <Typography variant="Body_Looko" color="error">
              {자동주문취소_스케줄러_시간}시간 이내에 ‘배송 준비중’으로 변경하지 않거나, 운송장번호를 입력하지 않으면,
              구매자가 주문을 취소할 수 있습니다.
            </Typography>
          </HStack>
        </VStack>
      </LookoModal.Content>

      <LookoModal.Actions>
        <LookoButton onClick={handleClickButton}>확인</LookoButton>
      </LookoModal.Actions>
    </LookoModal>
  );
}
